import { useState, useEffect } from 'react'
import PropTypes from '~/lib/propTypes'
import styled from '@emotion/styled'
import {
  device,
  space,
  shadow,
  color,
  fontSize,
  fontWeight,
  transition,
  radius,
  easing,
} from '@ticketswap/solar'
import FeedbackDialog from '~/components/FeedbackDialog'
import { Happy, Sad } from '@ticketswap/solar/icons'
import { useTranslation } from '~/components/TranslationContext'
import useIsMounted from '~/hooks/useIsMounted'

const Container = styled.div`
  display: none;

  @media ${device.tablet} {
    display: flex;
    flex-direction: column;
    position: fixed;
    inset-block-start: 50%;
    inset-inline-end: 0;
    background-color: ${color.background};
    padding-block: ${space[4]};
    padding-inline: ${space[4]};
    border-radius: ${space[8]} 0 0 ${space[8]};
    box-shadow: ${shadow.strong};
    transition-duration: 200ms;
    transition-timing-function: ${easing.easeOutCubic};
    transition-property: opacity, transform;
  }
`

const Button = styled.button`
  position: relative;
  border-radius: ${space[4]};
`

const IconContainer = styled.span`
  display: block;
  position: relative;
  color: ${color.lightForeground};
  border-radius: ${space[4]};
  background-color: ${props => (props.variant === 'danger' ? color.failure : color.success)};
  transition: background-color ${transition};
  outline: 0;
  font-size: 0;
  line-height: 0;

  &:first-of-type {
    margin-block-end: ${space[4]};
  }
`

const Tooltip = styled.span`
  visibility: hidden;
  pointer-events: none;
  position: absolute;
  margin-inline-end: ${space[16]};
  margin-block-start: ${space[8]};
  margin-block-end: ${space[8]};
  inset-inline-end: 100%;
  inset-block: 0;
  align-items: center;
  white-space: nowrap;
  font-size: ${fontSize[14]};
  font-weight: ${fontWeight.semiBold};
  opacity: 0;
  transform: translate3d(-0.5rem, 0, 0);
  transition: all ${transition};
  color: ${color.lightForegroundMuted};
  background-color: ${color.darkBackground};
  box-shadow: ${shadow.light};
  border-radius: ${radius.md};
  padding-block: ${space[8]};
  padding-inline: ${space[16]};

  span {
    display: inline-block;
    transform: translateY(1px);
  }

  button:hover &,
  button:focus & {
    visibility: visible;
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`

const FeedbackWidget = ({ initialShow = false }) => {
  const { t } = useTranslation()
  const [show, setShow] = useState(initialShow)
  const isMounted = useIsMounted()

  useEffect(() => {
    const observers = []

    if (isMounted()) {
      const elements = document.querySelectorAll('[data-hide-feedback-widget="true"]')

      elements.forEach(element => {
        const observer = new IntersectionObserver(
          entries => {
            setShow(!entries.some(entry => entry.isIntersecting))
          },
          {
            root: null,
            rootMargin: '-10px',
            threshold: 0.01,
          }
        )
        observer.observe(element)
        observers.push(observer)
      })
    }

    return () => observers.forEach(observer => observer.disconnect())
  }, [isMounted])

  return (
    <FeedbackDialog>
      {({ getToggleProps, setActiveView }) => (
        <Container
          style={{
            opacity: show ? 1 : 0,
            transform: show ? 'translate3d(0%,-50%,0)' : 'translate3d(100%,-50%,0)',
          }}
        >
          <Button
            {...getToggleProps({
              onClick: () =>
                setActiveView(
                  sessionStorage.getItem('positive_feedback_submitted')
                    ? 'positive-confirm'
                    : 'feedback'
                ),
            })}
          >
            <IconContainer>
              <Happy size={24} />
            </IconContainer>
            <IconContainer variant="danger">
              <Sad size={24} />
            </IconContainer>
            <Tooltip>
              <span>{t('common:feedbackDialog.title')}</span>
            </Tooltip>
          </Button>
        </Container>
      )}
    </FeedbackDialog>
  )
}

FeedbackWidget.propTypes = {
  initialShow: PropTypes.bool,
}

export default FeedbackWidget
